import React, { useEffect, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { Link, useNavigate } from "react-router-dom";
import { auth } from "../../lib/firebase";
import { pilotShow } from "../../data/gigs";
import { loadMyAudiencePassport } from "./audiencePassportApi";
import { signUpForPilotShow } from "./showSignupData";
import "./gigathlon.css";
import "./pilotShowSignup.css";

const PilotShowSignupPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [status, setStatus] = useState("checking");
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (cancelled) return;
      setUser(currentUser);
      if (!currentUser?.uid) {
        setStatus("ready");
        return;
      }

      try {
        const existingPassport = await loadMyAudiencePassport();
        if (cancelled) return;
        const hasPilotSignup = (existingPassport?.signups || []).some(
          (signup) => signup.showId === pilotShow.id && signup.status === "signed_up"
        );
        if (hasPilotSignup) {
          navigate("/passport", { replace: true });
          return;
        }
        setStatus("ready");
      } catch (passportError) {
        if (cancelled) return;
        if (passportError?.status === 404) {
          navigate("/passport/join?next=%2Fshows%2Fclass-clowns-reunion", { replace: true });
          return;
        }
        setError(passportError?.message || "We could not check your signup right now.");
        setStatus("ready");
      }
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [navigate]);

  const handleSignup = async () => {
    if (!user?.uid) {
      const params = new URLSearchParams({
        redirectTo: "/shows/class-clowns-reunion",
        journey: "audience_show_signup",
        participantType: "attendee",
        requiresOnboarding: "false",
      });
      navigate(`/login-phone?${params.toString()}`);
      return;
    }

    setStatus("saving");
    setError("");
    try {
      const result = await signUpForPilotShow(pilotShow.id);
      navigate("/passport", {
        replace: true,
        state: { showSignupComplete: true, passport: result.passport },
      });
    } catch (signupError) {
      setError(signupError?.message || "We could not complete your signup.");
      setStatus("ready");
    }
  };

  return (
    <main className="pilot-show-page">
      <header className="pilot-show-topbar">
        <Link to="/" className="gigathlon-wordmark" aria-label="Gigathlon home">
          G<span className="gigathlon-wordmark__mic" aria-hidden="true">🎤</span>g<span>athlon</span>
        </Link>
        <Link to="/passport">My Passport</Link>
      </header>

      <section className="pilot-show-layout" aria-labelledby="pilot-show-title">
        <div className="pilot-show-media">
          <img src={pilotShow.image} alt="Microphone in Valencia for Class Clowns Reunion" />
          <span>September 29 · Valencia</span>
        </div>
        <div className="pilot-show-copy">
          <p className="pilot-show-kicker">Participating show · Early Access</p>
          <h1 id="pilot-show-title">Class Clowns Reunion</h1>
          <p className="pilot-show-intro">A back-to-school comedy night at La Fabrica de Hielo, featuring stories from the best and worst days of the class.</p>

          <dl className="pilot-show-details">
            <div><dt>Date</dt><dd>Tuesday, September 29</dd></div>
            <div><dt>Venue</dt><dd>La Fabrica de Hielo</dd></div>
            <div><dt>Address</dt><dd>Carrer de Pavia, 37, Valencia</dd></div>
            <div><dt>Language</dt><dd>English</dd></div>
          </dl>

          <button className="pilot-show-signup" type="button" onClick={handleSignup} disabled={status === "saving" || status === "checking"}>
            {status === "checking" ? "Checking your signup..." : status === "saving" ? "Signing you up..." : user ? "Sign up for this show" : "Continue to sign up"}
          </button>
          <p className="pilot-show-ticket-note">Free Gigathlon signup. This does not reserve or purchase a venue ticket.</p>
          {error ? <p className="pilot-show-error" role="alert">{error}</p> : null}
        </div>
      </section>
    </main>
  );
};

export default PilotShowSignupPage;
